import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Wind, ArrowLeft, ShieldCheck, CreditCard, Zap } from "lucide-react";
import { useAuth } from "./AuthContext";
import { formatApiErrorDetail, SHIPPING_FEE } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUNDLES = {
  solo: { units: 1, label: "Solo", price: 39.99, strike: 69.99 },
  duo: { units: 2, label: "Duo", price: 69.99, strike: 79.98 },
  squad: { units: 3, label: "Squad", price: 89.99, strike: 119.97 },
};

export default function Checkout() {
  const [params] = useSearchParams();
  const initial = BUNDLES[params.get("bundle")] ? params.get("bundle") : "solo";
  const [bundle, setBundle] = useState(initial);
  const [cfg, setCfg] = useState({ stripe_enabled: false, paypal_enabled: false, client_id: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/payments/config`),
      axios.get(`${API}/paypal/config`),
    ]).then(([s, p]) => setCfg({ ...s.data, ...p.data })).catch(() => {});
  }, []);

  const b = BUNDLES[bundle];

  const payByCard = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        bundle,
        origin_url: window.location.origin,
        user_id: user?.id || null,
        email: user?.email || null,
      });
      window.location.assign(data.checkout_url);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-ink" data-testid="checkout-page">
      <div className="spotlight absolute inset-0" aria-hidden="true" />
      <header className="relative z-10 border-b border-steel">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-5 py-4 md:px-10">
          <Link to="/" className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest" data-testid="checkout-brand">
            <Wind className="h-5 w-5 text-volt" strokeWidth={2.5} />
            <span>X8<span className="text-volt">/</span>Violent Fan</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-smoke hover:text-volt" data-testid="checkout-back">
            <ArrowLeft className="h-3.5 w-3.5" /> Keep browsing
          </Link>
        </div>
      </header>

      <main className="relative z-10 mx-auto grid max-w-5xl gap-4 px-5 py-12 md:py-20 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <h1 className="font-display text-3xl font-black uppercase tracking-tight md:text-5xl">
            Lock in <span className="text-volt">your X8</span>
          </h1>
          <p className="mt-3 text-sm text-smoke">
            {user ? `Signed in as ${user.email}` : "Checking out as guest — "}
            {!user && <Link to="/auth" className="text-volt underline underline-offset-4" data-testid="checkout-signin-link">sign in for order tracking</Link>}
          </p>

          <div className="mt-10 grid gap-3" data-testid="bundle-selector">
            {Object.entries(BUNDLES).map(([key, t]) => (
              <button
                key={key}
                data-testid={`checkout-bundle-${key}`}
                onClick={() => setBundle(key)}
                className={`flex items-center justify-between border p-5 text-left transition-colors duration-200 ${
                  bundle === key ? "border-volt bg-volt/5" : "border-steel bg-carbon hover:border-smoke"
                }`}
              >
                <div>
                  <div className="font-display text-sm font-bold uppercase tracking-wide">
                    ×{t.units} — {t.label}
                    {key === "duo" && <span className="ml-3 bg-volt px-2 py-0.5 font-mono text-[9px] tracking-[0.2em] text-ink">MOST POPULAR</span>}
                  </div>
                  <div className="mt-1 font-mono text-[10px] tracking-[0.25em] uppercase text-smoke">
                    {t.units} × X8 Violent Fan · tracked shipping $4.99
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-lg font-bold text-volt">${t.price}</div>
                  <div className="font-mono text-[10px] text-smoke line-through">${t.strike}</div>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-10 border border-steel bg-carbon p-6 md:p-8" data-testid="payment-section">
            <h2 className="font-display text-sm font-bold uppercase tracking-wider">Payment</h2>

            <div className="mt-6">
              <button
                data-testid="pay-card-button"
                onClick={payByCard}
                disabled={loading || !cfg.stripe_enabled}
                className="flex w-full items-center justify-center gap-3 border border-volt bg-volt px-6 py-4 font-mono text-xs font-bold tracking-[0.2em] uppercase text-ink transition-colors duration-200 hover:bg-transparent hover:text-volt disabled:cursor-not-allowed disabled:opacity-50"
              >
                <CreditCard className="h-4 w-4" />
                {loading ? "Redirecting…" : cfg.stripe_enabled ? `Pay $${(b.price + SHIPPING_FEE).toFixed(2)} by card` : "Card payments activating soon"}
              </button>
              {!cfg.stripe_enabled && (
                <p className="mt-2 font-mono text-[10px] tracking-[0.15em] uppercase text-smoke" data-testid="stripe-pending-note">
                  Card checkout (Stripe) is being activated on this store — use PayPal or check back shortly.
                </p>
              )}
            </div>

            {cfg.paypal_enabled && (
              <div className="mt-6" data-testid="paypal-section">
                <div className="mb-3 flex items-center gap-3 font-mono text-[10px] tracking-[0.25em] uppercase text-smoke">
                  <span className="h-px flex-1 bg-steel" /> or <span className="h-px flex-1 bg-steel" />
                </div>
                <PayPalScriptProvider options={{ clientId: cfg.client_id, currency: "USD", intent: "capture" }}>
                  <PayPalButtons
                    style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                    forceReRender={[bundle]}
                    createOrder={async () => {
                      setError("");
                      const { data } = await axios.post(`${API}/paypal/orders`, { bundle, user_id: user?.id || null });
                      return data.id;
                    }}
                    onApprove={async (d) => {
                      try {
                        const { data } = await axios.post(`${API}/paypal/orders/${d.orderID}/capture`);
                        nav("/order-success", { state: { order: data.order } });
                      } catch (e) {
                        setError(formatApiErrorDetail(e.response?.data?.detail) || "PayPal capture failed");
                      }
                    }}
                    onError={() => setError("PayPal ran into an issue. Try card instead.")}
                  />
                </PayPalScriptProvider>
              </div>
            )}

            {error && <p data-testid="checkout-error" className="mt-4 font-mono text-xs text-red-400">{error}</p>}

            <p className="mt-6 flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] uppercase text-smoke">
              <ShieldCheck className="h-3.5 w-3.5 text-volt" /> 30-day money-back guarantee · secure payment
            </p>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="border border-steel bg-carbon p-6 md:p-8 lg:sticky lg:top-8" data-testid="order-summary">
            <h2 className="font-mono text-xs tracking-[0.3em] uppercase text-smoke">Order summary</h2>
            <div className="mt-6 flex items-center gap-4 border border-steel bg-ink p-4">
              <img src="/assets/cj-1.jpg" alt="X8 Violent Fan" className="h-16 w-16 border border-steel object-cover" />
              <div className="flex-1">
                <div className="font-display text-xs font-bold uppercase">X8 Violent Fan</div>
                <div className="mt-1 font-mono text-[10px] text-smoke">{b.label} — ×{b.units}</div>
              </div>
              <Zap className="h-4 w-4 text-volt" />
            </div>
            <div className="mt-6 grid gap-2 font-mono text-xs">
              <div className="flex justify-between"><span className="text-smoke">Subtotal</span><span>${b.price}</span></div>
              <div className="flex justify-between"><span className="text-smoke">Tracked shipping</span><span>${SHIPPING_FEE}</span></div>
              <div className="flex justify-between"><span className="text-smoke">You save</span><span className="text-volt">${(b.strike - b.price).toFixed(2)}</span></div>
              <div className="mt-2 flex justify-between border-t border-steel pt-4 text-base font-bold">
                <span>Total</span><span data-testid="checkout-total" className="text-volt">${(b.price + SHIPPING_FEE).toFixed(2)}</span>
              </div>
              <div className="text-right font-mono text-[10px] text-smoke">+ tax calculated at checkout</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
